import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Copy, Download, Pencil, Plus, Search, Trash2, UserRound } from 'lucide-react';
import { toast } from 'sonner';

const TAG_OPTIONS = ['VIP', 'unpaid', 'wholesale', 'parent', 'supplier', 'donor', 'member'];

const EMPTY_FORM = {
  name: '',
  company: '',
  phone: '',
  whatsapp: '',
  email: '',
  location: '',
  tags: '',
  lead_status: 'customer',
  follow_up_date: '',
  notes: '',
  status: 'active',
};

const LEAD_STATUS_OPTIONS = ['lead', 'prospect', 'customer', 'won', 'lost'];

function fmtMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-emerald-100 text-emerald-800';
  if (s === 'inactive') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

function paymentStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (['successful', 'success', 'paid', 'completed'].includes(s)) return 'bg-emerald-100 text-emerald-800';
  if (['pending', 'processing'].includes(s)) return 'bg-amber-100 text-amber-800';
  if (['failed', 'cancelled', 'canceled', 'rejected'].includes(s)) return 'bg-red-100 text-red-800';
  if (s === 'cash_on_delivery') return 'bg-blue-100 text-blue-800';
  if (s === 'whatsapp_order') return 'bg-violet-100 text-violet-800';
  if (s === 'online_payment') return 'bg-indigo-100 text-indigo-800';
  return 'bg-slate-100 text-slate-700';
}

function parseTags(value) {
  return String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .filter((tag, index, arr) => arr.indexOf(tag) === index);
}

function joinTags(tags) {
  if (!Array.isArray(tags)) return '';
  return tags.join(', ');
}

function CustomerHistorySection({ title, items, emptyLabel, renderRow, columns }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardHeader>
        <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {items.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column}>{column}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => renderRow(item, index))}
            </TableBody>
          </Table>
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">{emptyLabel}</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CustomersPage({ crmMode: crmModeProp = false } = {}) {
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState({ customers: [], access: {} });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [tag, setTag] = useState('all');
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const canUseCrm = !!data?.access?.can_use_crm;
  const crmMode = crmModeProp || location.pathname.startsWith('/dashboard/crm');

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/customers', {
        params: {
          q: search || undefined,
          status: status === 'all' ? undefined : status,
          tag: tag === 'all' ? undefined : tag,
        },
      });
      setData(res.data || { customers: [], access: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load customers');
      setData({ customers: [], access: err.response?.status === 403 ? { ...data.access } : {} });
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await api.get(`/dashboard/customers/${id}`);
      setDetail(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load customer details');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, tag]);

  useEffect(() => {
    const activeId = customerId || selectedId;
    if (activeId) {
      loadDetail(activeId);
    } else {
      setDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, selectedId]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setSelectedId('');
    setForm(EMPTY_FORM);
    navigate(crmMode ? '/dashboard/crm' : '/dashboard/customers');
  };

  const beginCreate = () => {
    setSelectedId('');
    setForm(EMPTY_FORM);
    navigate(crmMode ? '/dashboard/crm' : '/dashboard/customers');
  };

  const beginEdit = (customer) => {
    setSelectedId(customer.id);
    setForm({
      name: customer.name || '',
      company: customer.company || '',
      phone: customer.phone || '',
      whatsapp: customer.whatsapp || customer.phone || '',
      email: customer.email || '',
      location: customer.location || customer.address || '',
      tags: joinTags(customer.tags || []),
      lead_status: customer.lead_status || 'customer',
      follow_up_date: customer.follow_up_date || '',
      notes: customer.notes || '',
      status: customer.status || 'active',
    });
    navigate(crmMode ? `/dashboard/crm/${customer.id}` : `/dashboard/customers/${customer.id}`);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/dashboard/customers/export', {
        params: {
          q: search || undefined,
          status: status === 'all' ? undefined : status,
          tag: tag === 'all' ? undefined : tag,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jjuma-customers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV export ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export customers');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canUseCrm) return toast.error('Upgrade your package to use the CRM feature.');
    if (!form.name.trim()) return toast.error('Customer name is required.');
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        company: form.company,
        phone: form.phone,
        whatsapp: form.whatsapp,
        email: form.email,
        location: form.location,
        tags: parseTags(form.tags),
        lead_status: form.lead_status,
        follow_up_date: form.follow_up_date,
        notes: form.notes,
        status: form.status,
      };
      const res = selectedId
        ? await api.put(`/dashboard/customers/${selectedId}`, payload)
        : await api.post('/dashboard/customers', payload);
      toast.success(selectedId ? 'Customer updated' : 'Customer saved');
      const savedId = res.data?.id || selectedId;
      setSelectedId(savedId || '');
      setForm({
        name: res.data?.name || '',
        company: res.data?.company || '',
        phone: res.data?.phone || '',
        whatsapp: res.data?.whatsapp || '',
        email: res.data?.email || '',
        location: res.data?.location || res.data?.address || '',
        tags: joinTags(res.data?.tags || []),
        lead_status: res.data?.lead_status || 'customer',
        follow_up_date: res.data?.follow_up_date || '',
        notes: res.data?.notes || '',
        status: res.data?.status || 'active',
      });
      await loadList();
      if (savedId) {
        await loadDetail(savedId);
        navigate(crmMode ? `/dashboard/crm/${savedId}` : `/dashboard/customers/${savedId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (customer) => {
    if (!window.confirm('Archive this customer?')) return;
    try {
      await api.delete(`/dashboard/customers/${customer.id}`);
      toast.success('Customer archived');
      if (selectedId === customer.id) {
        resetForm();
      }
      await loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive customer');
    }
  };

  const toggleStatus = async (customer, nextStatus) => {
    try {
      await api.patch(`/dashboard/customers/${customer.id}/status`, { status: nextStatus });
      toast.success(`Customer marked ${nextStatus}`);
      await loadList();
      if ((customerId || selectedId) === customer.id) {
        await loadDetail(customer.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update customer status');
    }
  };

  const openCustomer = (customer) => {
    setSelectedId(customer.id);
    navigate(crmMode ? `/dashboard/crm/${customer.id}` : `/dashboard/customers/${customer.id}`);
  };

  const summaryCards = detail ? [
    { label: 'Total Paid', value: fmtMoney(detail.currency || 'UGX', detail.total_paid || 0) },
    { label: 'Total Orders', value: detail.total_orders ?? 0 },
    { label: 'Invoices', value: detail.invoice_count ?? 0 },
    { label: 'Payments', value: detail.payment_count ?? 0 },
  ] : [];

  if (loading && !data.customers.length) {
    return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{crmMode ? 'CRM' : 'Customers'}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{crmMode ? 'Manage leads, customers, notes and follow-ups.' : 'Track customers, payments, orders and follow-ups.'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportCsv}>
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={beginCreate}>
            <Plus size={14} className="mr-1" /> {crmMode ? 'Add Lead' : 'Add Customer'}
          </Button>
        </div>
      </div>

      {!canUseCrm && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Your current package does not allow CRM access. Upgrade your package to manage customers and leads.
        </div>
      )}

      {detailLoading && !detail && (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center">
            <div className="inline-flex h-6 w-6 animate-spin rounded-full border-2 border-emerald-700 border-t-transparent" />
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Search and filters</CardTitle>
          <div className="flex flex-wrap gap-3 mt-3">
            <div className="relative w-full sm:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers" className="pl-9" />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Tag" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {TAG_OPTIONS.map((tagOption) => (
                  <SelectItem key={tagOption} value={tagOption}>{tagOption}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {detail && (
        <>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <UserRound size={18} /> {detail.name || 'Customer'}
                </CardTitle>
                <p className="text-sm text-slate-500 mt-0.5 break-all">{detail.phone || detail.whatsapp || detail.email || '-'}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className={statusClass(detail.status)}>{detail.status || 'active'}</Badge>
                {(detail.tags || []).map((tagName) => (
                  <Badge key={tagName} className="bg-slate-100 text-slate-700">{tagName}</Badge>
                ))}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {summaryCards.map((item) => (
                  <div key={item.label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">{item.label}</p>
                    <p className="text-lg font-semibold text-slate-900 mono-nums">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600">
                <div><span className="text-slate-400">Company:</span> {detail.company || '-'}</div>
                <div><span className="text-slate-400">Location:</span> {detail.location || detail.address || '-'}</div>
                <div><span className="text-slate-400">WhatsApp:</span> {detail.whatsapp || '-'}</div>
                <div><span className="text-slate-400">Email:</span> {detail.email || '-'}</div>
                <div><span className="text-slate-400">Last Order:</span> {fmtDate(detail.last_order_date)}</div>
                <div><span className="text-slate-400">Last Payment:</span> {fmtDate(detail.last_payment_date)}</div>
                <div><span className="text-slate-400">Lead Status:</span> {detail.lead_status || '-'}</div>
                <div><span className="text-slate-400">Follow Up:</span> {fmtDate(detail.follow_up_date)}</div>
              </div>
              {detail.notes ? (
                <div>
                  <p className="text-xs text-slate-400 mb-1">Private notes</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-3">{detail.notes}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <CustomerHistorySection
              title="Invoices"
              items={detail.invoices || []}
              emptyLabel="No invoices yet."
              columns={['Invoice #', 'Title', 'Total', 'Status', 'Date']}
              renderRow={(invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell className="font-mono text-xs">{invoice.invoice_number || '-'}</TableCell>
                  <TableCell>{invoice.title || '-'}</TableCell>
                  <TableCell>{fmtMoney(invoice.currency, invoice.total || 0)}</TableCell>
                  <TableCell><Badge className={paymentStatusClass(invoice.status)}>{String(invoice.status || '-').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell>{fmtDate(invoice.created_at)}</TableCell>
                </TableRow>
              )}
            />
            <CustomerHistorySection
              title="Orders"
              items={detail.store_orders || []}
              emptyLabel="No store orders yet."
              columns={['Order #', 'Product', 'Total', 'Method', 'Date']}
              renderRow={(order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-xs">{order.order_number || order.reference_code || '-'}</TableCell>
                  <TableCell>{order.product_name || '-'}</TableCell>
                  <TableCell>{fmtMoney(order.currency, order.total || 0)}</TableCell>
                  <TableCell><Badge className={paymentStatusClass(order.order_method || order.payment_method)}>{String(order.order_method || order.payment_method || '-').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell>{fmtDate(order.created_at)}</TableCell>
                </TableRow>
              )}
            />
          </div>

          <CustomerHistorySection
            title="Payments"
            items={detail.payments || []}
            emptyLabel="No payment history yet."
            columns={['Reference', 'Link', 'Amount', 'Method', 'Status', 'Date']}
            renderRow={(payment) => (
              <TableRow key={payment.id || payment.transaction_id}>
                <TableCell className="font-mono text-xs">{payment.reference_code || payment.transaction_id || '-'}</TableCell>
                <TableCell>{payment.payment_link_title || payment.payment_link_slug || '-'}</TableCell>
                <TableCell>{fmtMoney(payment.currency, payment.amount || 0)}</TableCell>
                <TableCell>{payment.payment_method || '-'}</TableCell>
                <TableCell><Badge className={paymentStatusClass(payment.status)}>{String(payment.status || '-').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell>{fmtDate(payment.created_at)}</TableCell>
              </TableRow>
            )}
          />
        </>
      )}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedId ? 'Edit Customer' : 'Add Customer'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={save}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={form.company} onChange={(e) => update('company', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1" placeholder="256770000000" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} className="mt-1" placeholder="256770000000" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => update('location', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Lead Status</Label>
                <Select value={form.lead_status} onValueChange={(value) => update('lead_status', value)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Follow Up Date</Label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => update('follow_up_date', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(value) => update('status', value)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags</Label>
                <Input value={form.tags} onChange={(e) => update('tags', e.target.value)} className="mt-1" placeholder="VIP, donor, wholesale" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {TAG_OPTIONS.map((tagName) => (
                <Button
                  key={tagName}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const current = parseTags(form.tags);
                    if (!current.includes(tagName)) {
                      update('tags', [...current, tagName].join(', '));
                    }
                  }}
                >
                  {tagName}
                </Button>
              ))}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} className="mt-1 min-h-28" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                {saving ? 'Saving...' : selectedId ? 'Update Customer' : 'Save Customer'}
              </Button>
              {selectedId ? (
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserRound size={18} /> {crmMode ? 'CRM Leads' : 'Customer List'}
            </CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">{data.customers.length} record(s)</p>
          </div>
          {loading ? <div className="w-5 h-5 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /> : null}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Total Paid</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Lead</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Payment</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{customer.name || '-'}</div>
                    <div className="text-xs text-slate-500">{customer.company || ''}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-900">{customer.phone || customer.whatsapp || '-'}</div>
                    <div className="text-xs text-slate-500 break-all">{customer.email || '-'}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(customer.tags || []).slice(0, 3).map((tagName) => (
                        <Badge key={tagName} className="bg-slate-100 text-slate-700">{tagName}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{fmtMoney('UGX', customer.total_paid || 0)}</TableCell>
                  <TableCell>{customer.total_orders ?? 0}</TableCell>
                  <TableCell><Badge className="bg-sky-100 text-sky-800">{customer.lead_status || 'customer'}</Badge></TableCell>
                  <TableCell><Badge className={statusClass(customer.status)}>{customer.status || 'active'}</Badge></TableCell>
                  <TableCell>{fmtDate(customer.last_payment_date)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => openCustomer(customer)}>
                        <UserRound size={14} className="mr-1" /> View
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => beginEdit(customer)}>
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(customer.phone || customer.whatsapp || customer.email || '').then(() => toast.success('Copied')).catch(() => toast.error('Could not copy'))}>
                        <Copy size={14} className="mr-1" /> Copy
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => archive(customer)}>
                        <Trash2 size={14} className="mr-1" /> Archive
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!data.customers.length && !loading && (
            <div className="py-10 text-center text-sm text-slate-500">No customers yet.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
